import { ReactNode, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { logger } from '../utils/logger'
import { gateAutoRedirectUsed, gateLoginUrl, gateReturnAllowed, markGateAutoRedirect } from '../utils/gate'
import NoAccessPage from './NoAccessPage'

interface ProtectedRouteProps {
  children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, gateEnabled, accessDenied } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    logger.info('ProtectedRoute', 'Проверка авторизации', {
      isAuthenticated,
      isLoading,
      pathname: location.pathname
    })

    if (isLoading || isAuthenticated) return
    // Подпись верна, а допуска нет: показываем «нет доступа», на вход не отправляем (иначе цикл)
    if (accessDenied) return

    // Единый вход: один тихий переход за вкладку. Есть refresh-сессия — человек вернётся уже с cookie,
    // нет — «Вход» сам покажет форму. Если cookie так и не принялась, дальше обычная страница входа.
    if (gateEnabled && gateReturnAllowed() && !gateAutoRedirectUsed()) {
      markGateAutoRedirect()
      window.location.replace(gateLoginUrl(window.location.href))
      return
    }

    // Защита от пинг-понга: не редиректим если уже на /login
    if (location.pathname !== '/login') {
      logger.info('ProtectedRoute', 'Перенаправление на логин')
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, isLoading, gateEnabled, accessDenied, location.pathname, navigate])

  if (isLoading) {
    return null
  }
  if (!isAuthenticated && accessDenied) {
    return <NoAccessPage message={accessDenied} />
  }

  return isAuthenticated ? <>{children}</> : null
}
